import type { BootOptions, ExecResult, Kernel, KernelStatus } from "./Kernel";

type Pending = { resolve: (v: unknown) => void; reject: (e: Error) => void };

/** Ядро на Pyodide в Web Worker. Перезапуск и прерывание пересоздают воркер. */
export class PyodideKernel implements Kernel {
  private worker: Worker | null = null;
  private seq = 0;
  private pending = new Map<number, Pending>();
  private listeners = new Set<(s: KernelStatus) => void>();
  private st: KernelStatus = { state: "idle" };
  private opts: BootOptions = { packages: [], datasets: [] };
  private chain: Promise<unknown> = Promise.resolve();

  status() {
    return this.st;
  }

  subscribe(fn: (s: KernelStatus) => void) {
    this.listeners.add(fn);
    fn(this.st);
    return () => this.listeners.delete(fn);
  }

  private set(s: KernelStatus) {
    this.st = s;
    this.listeners.forEach((fn) => fn(s));
  }

  private spawn() {
    this.worker?.terminate();
    this.pending.forEach((p) => p.reject(new Error("ядро перезапущено")));
    this.pending.clear();
    const w = new Worker(new URL("./pyodide.worker.ts", import.meta.url), { type: "module" });
    w.onmessage = (e: MessageEvent<{ id?: number; ok?: boolean; result?: unknown; error?: string; type?: string; stage?: KernelStatus["stage"] }>) => {
      const m = e.data;
      if (m.type === "stage") {
        this.set({ ...this.st, state: "booting", stage: m.stage });
        return;
      }
      const p = m.id !== undefined ? this.pending.get(m.id) : undefined;
      if (!p) return;
      this.pending.delete(m.id!);
      if (m.ok) p.resolve(m.result);
      else p.reject(new Error(m.error ?? "ошибка ядра"));
    };
    w.onerror = (e) => {
      this.set({ state: "dead", error: e.message || "воркер упал" });
      this.pending.forEach((p) => p.reject(new Error("ядро упало")));
      this.pending.clear();
    };
    this.worker = w;
  }

  private call<T>(msg: Record<string, unknown>): Promise<T> {
    if (!this.worker) this.spawn();
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { resolve: resolve as (v: unknown) => void, reject });
      this.worker!.postMessage({ id, ...msg });
    });
  }

  async boot(opts: BootOptions) {
    this.opts = opts;
    this.spawn();
    this.set({ state: "booting", stage: "runtime", startedAt: Date.now() });
    try {
      await this.call({ type: "boot", opts });
      this.set({ state: "ready", startedAt: this.st.startedAt ?? Date.now() });
    } catch (e) {
      this.set({ state: "dead", error: e instanceof Error ? e.message : String(e) });
      throw e;
    }
  }

  /** Ячейки исполняются строго по очереди. */
  execute(code: string): Promise<ExecResult> {
    const run = this.chain.then(async () => {
      this.set({ ...this.st, state: "busy" });
      try {
        return await this.call<ExecResult>({ type: "exec", code });
      } finally {
        if (this.st.state === "busy") this.set({ ...this.st, state: "ready" });
      }
    });
    this.chain = run.catch(() => undefined);
    return run;
  }

  async restart() {
    this.chain = Promise.resolve();
    await this.boot(this.opts);
  }

  interrupt() {
    return this.restart();
  }

  dispose() {
    this.worker?.terminate();
    this.worker = null;
    this.set({ state: "idle" });
  }
}
